import { inject, Injectable } from '@angular/core';
import {
  deleteEntities,
  selectAllEntities,
  selectEntity,
  setEntities,
  upsertEntities,
} from '@ngneat/elf-entities';
import { PreferenceService } from '@thiememeulenhoff/ng-api-gateway';
import { MethodPreference } from '@thiememeulenhoff/types';
import { delayWhen, tap, timer } from 'rxjs';
import * as stores from './preference.store';

@Injectable({
  providedIn: 'root',
})
export class PreferenceFacade {
  readonly #methodPreferences = stores.methodPreferences;

  readonly #preferenceService = inject(PreferenceService);

  readonly methodPreferences$ =
    this.#methodPreferences.pipe(selectAllEntities());

  readonly methodPreferenceById$ = (method: string) =>
    this.#methodPreferences.pipe(selectEntity(method));

  fetchMethodPreferences() {
    return this.#preferenceService
      .fetchPreferences()
      .pipe(tap(this.setMethodPreferences));
  }

  setMethodPreference(preference: MethodPreference) {
    return this.#preferenceService.setMethodPreference(preference).pipe(
      tap(() => this.#upsertMethodPreferences([preference])),
      tap(() => this.fetchMethodPreferences().subscribe()),
    );
  }

  deleteMethodPreference(methodSlug: string) {
    return this.#preferenceService.deleteMethodPreference(methodSlug).pipe(
      tap(() => this.#deleteMethodPreference(methodSlug)),
      tap(() => this.fetchMethodPreferences().subscribe()),
    );
  }

  addArrangementToMethodPreference(
    methodSlug: string,
    arrangementCode: string,
  ) {
    return this.#preferenceService
      .addArrangementToMethodPreference(methodSlug, arrangementCode)
      .pipe(tap(() => this.fetchMethodPreferences().subscribe()));
  }

  deleteArrangementFromMethodPreference(
    methodSlug: string,
    arrangementCode: string,
    delayStateChangeInMs = 0,
  ) {
    const currentTimestamp = Date.now();

    return this.#preferenceService
      .deleteArrangementFromMethodPreference(methodSlug, arrangementCode)
      .pipe(
        delayWhen(() => {
          const delayUntil =
            delayStateChangeInMs - (Date.now() - currentTimestamp);

          if (delayUntil <= 0) {
            return timer(0);
          }

          return timer(delayUntil);
        }),
        tap(() => this.fetchMethodPreferences().subscribe()),
      );
  }

  // #region Store mutations
  setMethodPreferences = (preferences: Array<MethodPreference>) => {
    this.#methodPreferences.update(setEntities(preferences));
  };

  // #region Store mutations
  #upsertMethodPreferences = (preferences: Array<MethodPreference>) => {
    this.#methodPreferences.update(upsertEntities(preferences));
  };

  #deleteMethodPreference = (method: string) => {
    this.#methodPreferences.update(deleteEntities(method));
  };
  // #endregion
}
